import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { UserMenu } from "./UserMenu";
import { GlobalSearch } from "./GlobalSearch";
import { AlertsBadge } from "./AlertsBadge";
import { useAuthStore } from "../state/authStore";

const NAV_LINKS = [
    { to: "/about", label: "About", id: "about" },
    { to: "/tools", label: "Tools", id: "tools" },
    { to: "/pricing", label: "Pricing", id: "pricing" },
    { to: "/contact", label: "Contact", id: "contact" },
];

const AUTH_NAV_LINKS = [
    { to: "/clients", label: "Clients", id: "clients" },
];

const linkBase =
    "relative text-[13px] font-medium tracking-wide transition-colors duration-200";
const linkInactive = "text-text-muted hover:text-text-primary";
const linkActive = "text-text-primary";

export const Nav = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const user = useAuthStore((s) => s.user);

    return (
        <header
            className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-canvas/70 border-b border-white/[0.05]"
            data-testid="site-nav"
        >
            <div className="mx-auto max-w-7xl px-5 md:px-10 py-3 flex items-center justify-between">
                <Logo size="md" />

                <nav className="hidden md:flex items-center gap-7" data-testid="nav-links">
                    {NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.id}
                            to={l.to}
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? linkActive : linkInactive}`
                            }
                            data-testid={`nav-link-${l.id}`}
                        >
                            {({ isActive }) => (
                                <>
                                    <span>{l.label}</span>
                                    <span
                                        aria-hidden
                                        className="absolute -bottom-1 left-0 h-px bg-accent-cyan transition-all duration-300 ease-out"
                                        style={{
                                            width: isActive ? "100%" : "0%",
                                            boxShadow: isActive
                                                ? "0 0 8px rgba(3,216,243,0.7)"
                                                : "none",
                                        }}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                    {user && AUTH_NAV_LINKS.map((l) => (
                        <NavLink
                            key={l.id}
                            to={l.to}
                            className={({ isActive }) =>
                                `${linkBase} ${isActive ? linkActive : linkInactive}`
                            }
                            data-testid={`nav-link-${l.id}`}
                        >
                            {({ isActive }) => (
                                <>
                                    <span>{l.label}</span>
                                    <span
                                        aria-hidden
                                        className="absolute -bottom-1 left-0 h-px bg-accent-cyan transition-all duration-300 ease-out"
                                        style={{
                                            width: isActive ? "100%" : "0%",
                                            boxShadow: isActive
                                                ? "0 0 8px rgba(3,216,243,0.7)"
                                                : "none",
                                        }}
                                    />
                                </>
                            )}
                        </NavLink>
                    ))}
                    {user ? (
                        <>
                            <GlobalSearch />
                            <AlertsBadge user={user} />
                            <UserMenu />
                        </>
                    ) : (
                        <NavLink
                            to="/login"
                            className={({ isActive }) =>
                                `text-[13px] font-medium px-3.5 py-1.5 rounded-md border transition-colors duration-200 ${
                                    isActive
                                        ? "border-accent-cyan/70 bg-accent-cyan/10 text-accent-cyan"
                                        : "border-accent-cyan/30 text-accent-cyan hover:bg-accent-cyan/10 hover:border-accent-cyan/60"
                                }`
                            }
                            data-testid="nav-link-login"
                        >
                            Login
                        </NavLink>
                    )}
                </nav>

                {/* Mobile hamburger */}
                <button
                    type="button"
                    className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md border border-white/[0.06] text-text-muted hover:text-text-primary hover:border-white/[0.15] transition-colors duration-200"
                    aria-label={mobileOpen ? "Close menu" : "Open menu"}
                    aria-expanded={mobileOpen}
                    onClick={() => setMobileOpen((v) => !v)}
                    data-testid="nav-mobile-toggle"
                >
                    {mobileOpen ? (
                        <X className="w-4 h-4" />
                    ) : (
                        <Menu className="w-4 h-4" />
                    )}
                </button>
            </div>

            {/* Mobile drawer */}
            {mobileOpen && (
                <div
                    className="md:hidden border-t border-white/[0.05] bg-canvas/95 backdrop-blur-xl"
                    data-testid="nav-mobile-drawer"
                >
                    <div className="mx-auto max-w-7xl px-5 py-4 flex flex-col gap-3">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `text-sm font-medium py-1.5 ${
                                        isActive
                                            ? "text-accent-cyan"
                                            : "text-text-muted"
                                    }`
                                }
                                data-testid={`nav-link-${l.id}-mobile`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {user && AUTH_NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.id}
                                to={l.to}
                                onClick={() => setMobileOpen(false)}
                                className={({ isActive }) =>
                                    `text-sm font-medium py-1.5 ${
                                        isActive ? "text-accent-cyan" : "text-text-muted"
                                    }`
                                }
                                data-testid={`nav-link-${l.id}-mobile`}
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <NavLink
                            to="/login"
                            onClick={() => setMobileOpen(false)}
                            className="mt-2 text-sm font-medium px-3.5 py-2 rounded-md border border-accent-cyan/30 text-accent-cyan text-center"
                            data-testid="nav-link-login-mobile"
                        >
                            Login
                        </NavLink>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Nav;
